import { ORCHESTRATOR_AUTOFLOW_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  AddToolRequest,
  OrchestratorAutoflowWorkflowModel,
  OrchestratorAutoflowWorkflowNameModel,
} from './types';
import {
  AutoflowTool,
  FlowDiagramNode,
  FlowDiagramResponse,
} from 'src/types/workflowBuilderAPITypes';

export interface GetOrCreateOrchestratorAutoflowWorkflowParams {
  stepId: string;
  workflowId: string;
}

export const orchestratorAutoflowApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    addOrchestratorAutoflowContextVariable: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflow: OrchestratorAutoflowWorkflowModel;
        contextVariableId: string;
      }
    >({
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      async onQueryStarted(
        { autoflowWorkflow, contextVariableId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          orchestratorAutoflowApi.util.updateQueryData(
            'getOrCreateOrchestratorAutoflowWorkflow',
            {
              stepId: autoflowWorkflow.orchestrator_workflow_step_id,
              workflowId: autoflowWorkflow.orchestrator_workflow_id,
            },
            draft => {
              draft.context_variables = [
                ...draft.context_variables,
                {
                  context_variable_id: contextVariableId,
                  context_variable_name: '',
                },
              ];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autoflowWorkflow, contextVariableId }) => ({
        body: { context_variable_id: contextVariableId },
        method: 'PUT',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflow.orchestrator_autoflow_workflow_id}/context-variables`,
      }),
    }),

    addOrchestratorAutoflowToolDefinition: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflow: OrchestratorAutoflowWorkflowModel;
        request: AddToolRequest;
      }
    >({
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      async onQueryStarted(
        { autoflowWorkflow, request },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          orchestratorAutoflowApi.util.updateQueryData(
            'getOrCreateOrchestratorAutoflowWorkflow',
            {
              stepId: autoflowWorkflow.orchestrator_workflow_step_id,
              workflowId: autoflowWorkflow.orchestrator_workflow_id,
            },
            draft => {
              draft.tool_definitions = [
                ...draft.tool_definitions,
                {
                  description: '',
                  name: '',
                  tool_fields: {},
                  tool_id: request.tool_id,
                  tool_type: request.tool_type,
                },
              ];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autoflowWorkflow, request }) => ({
        body: request,
        method: 'PUT',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflow.orchestrator_autoflow_workflow_id}/tools`,
      }),
    }),

    getOrchestratorAutoflowAvailableTools: builder.query<AutoflowTool[], void>({
      query: () => ({
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/available-tools`,
      }),
    }),

    getOrchestratorAutoflowFlowDiagram: builder.query<
      FlowDiagramResponse,
      { orchestratorAutoflowWorkflowId: string }
    >({
      providesTags: (_result, _error, { orchestratorAutoflowWorkflowId }) => [
        { id: orchestratorAutoflowWorkflowId, type: 'FlowDiagram' },
      ],
      query: ({ orchestratorAutoflowWorkflowId }) => ({
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${orchestratorAutoflowWorkflowId}/flow-diagram`,
      }),
    }),

    getOrchestratorAutoflowWorkflowNames: builder.query<
      OrchestratorAutoflowWorkflowNameModel[],
      { workflowId: string }
    >({
      providesTags: ['OrchestratorAutoflowWorkflowNames'],
      query: ({ workflowId }) => ({
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/workflow/${workflowId}/autoflow-workflow-names`,
      }),
    }),

    getOrCreateOrchestratorAutoflowWorkflow: builder.query<
      OrchestratorAutoflowWorkflowModel,
      GetOrCreateOrchestratorAutoflowWorkflowParams
    >({
      providesTags: ['OrchestratorAutoflowWorkflow'],
      query: ({ stepId, workflowId }) => ({
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/workflow/${workflowId}/step/${stepId}/autoflow-workflow`,
      }),
    }),

    refreshOrchestratorAutoflowFlowDiagram: builder.mutation<
      FlowDiagramResponse,
      {
        orchestratorAutoflowWorkflowId: string;
        previousOutline: FlowDiagramNode[];
      }
    >({
      // Write the response into the GET cache so subscribers see the new
      // outline without a refetch. `upsertQueryData` (rather than
      // `updateQueryData`) marks the cache entry as `fulfilled` — important
      // when the previous GET errored, since `updateQueryData` only swaps
      // `data` and would leave the error set, keeping the panel stuck on the
      // http-error view even after a successful refresh.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            orchestratorAutoflowApi.util.upsertQueryData(
              'getOrchestratorAutoflowFlowDiagram',
              {
                orchestratorAutoflowWorkflowId:
                  args.orchestratorAutoflowWorkflowId,
              },
              data,
            ),
          );
        } catch {
          // Errors surface via the panel's error state.
        }
      },
      query: ({ orchestratorAutoflowWorkflowId, previousOutline }) => ({
        body: { previous_outline: previousOutline },
        method: 'POST',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${orchestratorAutoflowWorkflowId}/flow-diagram/refresh`,
      }),
    }),

    removeOrchestratorAutoflowContextVariable: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflow: OrchestratorAutoflowWorkflowModel;
        contextVariableId: string;
      }
    >({
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      async onQueryStarted(
        { autoflowWorkflow, contextVariableId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          orchestratorAutoflowApi.util.updateQueryData(
            'getOrCreateOrchestratorAutoflowWorkflow',
            {
              stepId: autoflowWorkflow.orchestrator_workflow_step_id,
              workflowId: autoflowWorkflow.orchestrator_workflow_id,
            },
            draft => {
              draft.context_variables = draft.context_variables.filter(
                cv => cv.context_variable_id !== contextVariableId,
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autoflowWorkflow, contextVariableId }) => ({
        body: { context_variable_id: contextVariableId },
        method: 'DELETE',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflow.orchestrator_autoflow_workflow_id}/context-variables`,
      }),
    }),

    removeOrchestratorAutoflowToolDefinition: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflow: OrchestratorAutoflowWorkflowModel;
        toolId: string;
      }
    >({
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      async onQueryStarted(
        { autoflowWorkflow, toolId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          orchestratorAutoflowApi.util.updateQueryData(
            'getOrCreateOrchestratorAutoflowWorkflow',
            {
              stepId: autoflowWorkflow.orchestrator_workflow_step_id,
              workflowId: autoflowWorkflow.orchestrator_workflow_id,
            },
            draft => {
              draft.tool_definitions = draft.tool_definitions.filter(
                tool => tool.tool_id !== toolId,
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autoflowWorkflow, toolId }) => ({
        method: 'DELETE',
        params: { tool_id: toolId },
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflow.orchestrator_autoflow_workflow_id}/tools`,
      }),
    }),

    updateOrchestratorAutoflowName: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflowId: string;
        workflowName: string;
      }
    >({
      invalidatesTags: [
        'OrchestratorAutoflowWorkflow',
        'OrchestratorAutoflowWorkflowNames',
      ],
      async onQueryStarted(
        { autoflowWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            orchestratorAutoflowApi.util.updateQueryData(
              'getOrchestratorAutoflowWorkflowNames',
              {
                workflowId: data.orchestrator_workflow_id,
              },
              draft => {
                const existing = draft.find(
                  item =>
                    item.orchestrator_autoflow_workflow_id ===
                    autoflowWorkflowId,
                );
                if (existing) {
                  existing.workflow_name = data.workflow_name;
                } else {
                  draft.push({
                    orchestrator_autoflow_workflow_id: autoflowWorkflowId,
                    orchestrator_workflow_step_id:
                      data.orchestrator_workflow_step_id,
                    workflow_name: data.workflow_name,
                  });
                }
              },
            ),
          );
        } catch {
          // rely on invalidation when request fails
        }
      },
      query: ({ autoflowWorkflowId, workflowName }) => ({
        body: { workflow_name: workflowName },
        method: 'PUT',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflowId}/name`,
      }),
    }),

    updateOrchestratorAutoflowPolicyDescription: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflowId: string;
        policyDescription: string;
      }
    >({
      // Intentionally does NOT invalidate FlowDiagram — see the matching
      // comment in workflowBuilderAutoflowApi.updatePolicyDescription.
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      query: ({ autoflowWorkflowId, policyDescription }) => ({
        body: { policy_description: policyDescription },
        method: 'PUT',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflowId}/policy-description`,
      }),
    }),

    updateOrchestratorAutoflowToolConfigurationFields: builder.mutation<
      OrchestratorAutoflowWorkflowModel,
      {
        autoflowWorkflowId: string;
        configurationFields: Record<string, unknown>;
        toolId: string;
      }
    >({
      invalidatesTags: ['OrchestratorAutoflowWorkflow'],
      query: ({ autoflowWorkflowId, configurationFields, toolId }) => ({
        body: { configuration_fields: configurationFields },
        method: 'PUT',
        url: `${ORCHESTRATOR_AUTOFLOW_BASE_URL}/autoflow-workflow/${autoflowWorkflowId}/tools/${toolId}/configuration-fields`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddOrchestratorAutoflowContextVariableMutation,
  useAddOrchestratorAutoflowToolDefinitionMutation,
  useGetOrchestratorAutoflowAvailableToolsQuery,
  useGetOrchestratorAutoflowFlowDiagramQuery,
  useGetOrchestratorAutoflowWorkflowNamesQuery,
  useGetOrCreateOrchestratorAutoflowWorkflowQuery,
  useRefreshOrchestratorAutoflowFlowDiagramMutation,
  useRemoveOrchestratorAutoflowContextVariableMutation,
  useRemoveOrchestratorAutoflowToolDefinitionMutation,
  useUpdateOrchestratorAutoflowNameMutation,
  useUpdateOrchestratorAutoflowPolicyDescriptionMutation,
  useUpdateOrchestratorAutoflowToolConfigurationFieldsMutation,
} = orchestratorAutoflowApi;
